import ConditionRow from './ConditionRow';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, FolderPlus, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ConditionGroup, Condition } from '../../../types/rule.types';

interface ConditionGroupEditorProps {
  group: ConditionGroup;
  onChange: (updated: ConditionGroup) => void;
  onRemove?: () => void;
  depth?: number;
}

function generateId() {
  return 'c-' + Math.random().toString(36).slice(2, 9);
}

export default function ConditionGroupEditor({ group, onChange, onRemove, depth = 0 }: ConditionGroupEditorProps) {
  const toggleLogic = () => {
    onChange({ ...group, logic: group.logic === 'AND' ? 'OR' : 'AND' });
  };

  const updateCondition = (idx: number, updated: Condition) => {
    const newConditions = [...group.conditions];
    newConditions[idx] = updated;
    onChange({ ...group, conditions: newConditions });
  };

  const removeCondition = (idx: number) => {
    onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== idx) });
  };

  const addCondition = () => {
    const newCondition: Condition = {
      id: generateId(),
      propertyId: 'matter.status',
      operator: 'equals',
      value: '',
    };
    onChange({ ...group, conditions: [...group.conditions, newCondition] });
  };

  const addNestedGroup = () => {
    const newGroup: ConditionGroup = {
      id: 'cg-' + Math.random().toString(36).slice(2, 9),
      logic: group.logic === 'AND' ? 'OR' : 'AND',
      conditions: [{ id: generateId(), propertyId: 'matter.status', operator: 'equals', value: '' }],
      groups: [],
    };
    onChange({ ...group, groups: [...group.groups, newGroup] });
  };

  const updateNestedGroup = (idx: number, updated: ConditionGroup) => {
    const newGroups = [...group.groups];
    newGroups[idx] = updated;
    onChange({ ...group, groups: newGroups });
  };

  const removeNestedGroup = (idx: number) => {
    onChange({ ...group, groups: group.groups.filter((_, i) => i !== idx) });
  };

  const borderColor = depth === 0 ? 'border-border' : depth === 1 ? 'border-email/20' : 'border-draft/20';

  return (
    <div className={cn("rounded-lg border p-3 space-y-2", borderColor, depth > 0 ? "bg-muted/30" : "bg-studio-surface")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLogic}
            className={cn(
              "px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors",
              group.logic === 'AND' ? "bg-email/10 text-email hover:bg-email/20" : "bg-draft/10 text-draft hover:bg-draft/20"
            )}
          >
            {group.logic}
          </button>
          <span className="text-[11px] text-muted-foreground">
            {group.logic === 'AND' ? 'All conditions must match' : 'Any condition must match'}
          </span>
        </div>
        {onRemove && (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={onRemove}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="space-y-1.5 pl-1">
        {group.conditions.map((condition, idx) => (
          <div key={condition.id} className="flex items-center gap-2">
            {idx > 0 && (
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 font-semibold uppercase tracking-widest border-none text-muted-foreground w-8 justify-center shrink-0">
                {group.logic}
              </Badge>
            )}
            {idx === 0 && <div className="w-8 shrink-0" />}
            <ConditionRow
              condition={condition}
              onChange={(updated) => updateCondition(idx, updated)}
              onRemove={() => removeCondition(idx)}
              isOnly={group.conditions.length <= 1 && group.groups.length === 0}
            />
          </div>
        ))}
      </div>

      {group.groups.map((nested, idx) => (
        <div key={nested.id} className="pl-9">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 font-semibold uppercase tracking-widest border-none text-muted-foreground">
              {group.logic}
            </Badge>
          </div>
          <ConditionGroupEditor
            group={nested}
            onChange={(updated) => updateNestedGroup(idx, updated)}
            onRemove={() => removeNestedGroup(idx)}
            depth={depth + 1}
          />
        </div>
      ))}

      <div className="flex items-center gap-2 pl-9 pt-1">
        <Button variant="ghost" size="sm" className="h-6 text-[11px] text-muted-foreground hover:text-foreground" onClick={addCondition}>
          <Plus className="h-3 w-3 mr-1" />
          Condition
        </Button>
        {depth < 2 && (
          <Button variant="ghost" size="sm" className="h-6 text-[11px] text-muted-foreground hover:text-foreground" onClick={addNestedGroup}>
            <FolderPlus className="h-3 w-3 mr-1" />
            Group
          </Button>
        )}
      </div>
    </div>
  );
}
